import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound } from "@/pages/ErrorPages";
import ContentRow from "@/components/ContentRow";
import { Play, Plus, Check, Film, Loader2, Lock, ShoppingBag, Clock } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";

export default function ContentDetail() {
  const { contentSlug } = useParams();
  const { slug, sp } = useStore();
  const [params] = useSearchParams();
  const upgradeAnchor = params.get("upgrade") === "1";
  const { user, logout, status } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [season, setSeason] = useState(1);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [entitlements, setEntitlements] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/public/tenants/${slug}/content/${contentSlug}`);
      setData(data);
      if (data.content.kind === "series" && data.content.seasons?.length) {
        setSeason(data.content.seasons[0].season_number);
      }
      setPageStatus("ready");
    } catch (_e) {
      setPageStatus("not_found");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, contentSlug]);

  useEffect(() => {
    if (!data?.content?.id || status !== "authenticated") return;
    api.get("/me/watchlist")
      .then((r) => {
        setInWatchlist(r.data.some((w) => w.content_item_id === data.content.id));
      })
      .catch(() => {});
    api.get("/me/entitlements")
      .then((r) => setEntitlements(r.data))
      .catch(() => {});
  }, [data?.content?.id, status]);

  // Scroll to pricing block if redirected from a paywall
  useEffect(() => {
    if (upgradeAnchor && data) {
      const el = document.querySelector('[data-testid="detail-pricing-block"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [upgradeAnchor, data]);

  // Phase 4: fire `paywall_view` event when an upgrade redirect lands on this page
  // and the viewer does not yet have access. One event per (content, mount).
  const [paywallReported, setPaywallReported] = useState(false);
  useEffect(() => {
    if (!upgradeAnchor || !data || paywallReported) return;
    if (status === "authenticated" && entitlements === null) return; // wait until access state is known
    const c = data.content;
    if (!c) return;
    const hasSub = !!entitlements?.has_active_subscription;
    const owned = entitlements?.owned_content_ids?.includes(c.id);
    const rented = entitlements?.rented?.find((r) => r.content_id === c.id);
    const isAdminOfTenant = !!(user && (user.role === "tenant_admin" || user.role === "super_admin") && user.tenant_id === c.tenant_id);
    const hasAccess = status === "authenticated" && (hasSub || owned || !!rented || isAdminOfTenant);
    if (hasAccess) return; // only fire when actually paywalled
    setPaywallReported(true);
    api.post("/events/view", {
      tenant_slug: slug,
      content_item_id: c.id,
      source: c.kind === "series" ? "series" : "movie",
      kind: "paywall_view",
      position_seconds: 0,
      duration_seconds: 0,
      session_id: typeof crypto !== "undefined" && crypto.randomUUID
        ? `pw-${crypto.randomUUID()}`
        : `pw-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradeAnchor, data, entitlements, status, user, paywallReported, slug]);

  if (pageStatus === "loading") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-sm uppercase tracking-widest">
        Loading…
      </div>
    );
  }
  if (pageStatus === "not_found" || !data) return <NotFound />;

  const tenant = data.tenant;
  const c = data.content;
  const themeColor = tenant.theme_color;
  const accentColor = tenant.accent_color;

  // Resolve access for THIS content
  const hasSub = !!entitlements?.has_active_subscription;
  const owned = entitlements?.owned_content_ids?.includes(c.id);
  const rented = entitlements?.rented?.find((r) => r.content_id === c.id);
  const rentExpiresAt = rented?.expires_at;
  const hasAnyAccess = status === "authenticated" && (hasSub || owned || !!rented || (user && (user.role === "tenant_admin" || user.role === "super_admin") && user.tenant_id === c.tenant_id));

  const isLoggedOut = status !== "authenticated";
  const rentPrice = c.tvod_rent_price;
  const buyPrice = c.tvod_buy_price;

  const onPlay = () => {
    if (status !== "authenticated") {
      navigate(sp(`/login?next=${encodeURIComponent(sp(`/watch/${contentSlug}`))}`));
      return;
    }
    if (!hasAnyAccess) {
      // Scroll to pricing
      navigate(sp(`/title/${contentSlug}?upgrade=1`), { replace: true });
      const el = document.querySelector('[data-testid="detail-pricing-block"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (c.kind === "series") {
      const ep = (c.episodes || []).filter((e) => e.season_number === season).sort((a, b) => a.episode_number - b.episode_number)[0];
      if (ep) navigate(sp(`/watch/${contentSlug}/s${ep.season_number}/e${ep.episode_number}`));
    } else {
      navigate(sp(`/watch/${contentSlug}`));
    }
  };

  const toggleWatchlist = async () => {
    if (status !== "authenticated") {
      navigate(sp(`/login?next=${encodeURIComponent(sp(`/title/${contentSlug}`))}`));
      return;
    }
    setBusy(true);
    try {
      if (inWatchlist) {
        await api.delete(`/me/watchlist/${c.id}`);
        setInWatchlist(false);
        toast.success("Removed from My list");
      } else {
        await api.post(`/me/watchlist`, { content_item_id: c.id });
        setInWatchlist(true);
        toast.success("Added to My list");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const seasonsList = c.seasons || [];
  const episodesForSeason = (c.episodes || []).filter((e) => e.season_number === season);

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav={status === "authenticated"}>
      <section className="relative isolate overflow-hidden grain" data-testid="content-detail">
        <div className="absolute inset-0 -z-10">
          {(c.hero_image_url || c.thumbnail_url) ? (
            <img src={c.hero_image_url || c.thumbnail_url} alt="" className="w-full h-full object-cover opacity-35" />
          ) : null}
          <div
            className="absolute inset-0"
            style={{ background: `radial-gradient(ellipse at top right, ${themeColor}55, transparent 50%), linear-gradient(180deg, rgba(10,10,10,0.4), #0a0a0a 90%)` }}
          />
        </div>
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-20 pb-16 md:pt-24 md:pb-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 border text-[10px] uppercase tracking-[0.3em]" style={{ borderColor: `${accentColor}55`, color: accentColor, backgroundColor: `${accentColor}15` }}>
              {c.kind}
            </span>
            <h1 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]" data-testid="detail-title">
              {c.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-white/60">
              {c.release_year && <span>{c.release_year}</span>}
              {c.age_rating && <span className="px-1.5 py-0.5 border border-white/20">{c.age_rating}</span>}
              {c.duration_minutes && <span>{c.duration_minutes} min</span>}
              {(c.languages || []).slice(0, 3).map((l) => <span key={l}>{l}</span>)}
            </div>
            <p className="mt-5 max-w-2xl text-white/70 font-light text-sm md:text-base leading-relaxed">{c.description}</p>
            {(c.genres || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {c.genres.map((g) => (
                  <span key={g} className="text-[10px] uppercase tracking-[0.25em] px-2 py-1 bg-white/5 border border-white/10 text-white/70">{g}</span>
                ))}
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={onPlay} className="px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5" style={{ backgroundColor: themeColor, color: "#0a0a0a" }} data-testid="detail-play-button">
                {hasAnyAccess ? <Play className="w-4 h-4 fill-black" /> : <Lock className="w-4 h-4" />}
                {hasAnyAccess ? (c.kind === "series" ? `Play S${season}E1` : "Play") : "Unlock to play"}
              </button>
              <button onClick={toggleWatchlist} disabled={busy} className="px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 text-white/80 hover:text-white border border-white/15 hover:border-white/40" data-testid="detail-watchlist-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : inWatchlist ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {inWatchlist ? "On My list" : "Add to My list"}
              </button>
            </div>
            {rentExpiresAt && hasAnyAccess && (
              <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-300">
                <Clock className="w-3.5 h-3.5" /> Rent active · expires {new Date(rentExpiresAt).toLocaleString()}
              </div>
            )}
            {(c.cast || []).length > 0 && (
              <div className="mt-8 text-xs text-white/55">
                <span className="text-white/40 uppercase tracking-[0.3em] mr-2">Cast</span>
                {c.cast.join(" · ")}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing block — shows subscribe / rent / buy CTAs based on what's available + user state */}
      {!hasAnyAccess && (
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 -mt-2 pb-2">
          <div className="bg-[#121212] border border-white/10 p-6 md:p-8" data-testid="detail-pricing-block">
            <div className="flex items-start gap-3 mb-5">
              <Lock className="w-5 h-5 mt-0.5" style={{ color: themeColor }} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-white/40">Unlock {c.title}</div>
                <h3 className="font-display font-bold text-2xl tracking-tight mt-1">Choose how you'd like to watch</h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Link
                to={isLoggedOut ? sp(`/login?next=${encodeURIComponent(sp(`/plans`))}`) : sp(`/plans`)}
                className="bg-black/40 border p-5 hover:-translate-y-0.5 transition-transform"
                style={{ borderColor: `${themeColor}55` }}
                data-testid="pricing-cta-subscribe"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Subscription</div>
                <div className="font-display font-bold text-xl mt-2">Full catalogue</div>
                <div className="text-xs text-white/50 mt-1">{tenant.name} library, monthly or yearly.</div>
                <div className="mt-4 inline-flex px-3 py-1.5 text-[10px] uppercase tracking-[0.25em]" style={{ backgroundColor: themeColor, color: "#0a0a0a" }}>
                  See plans
                </div>
              </Link>
              {rentPrice && (
                <Link
                  to={isLoggedOut
                    ? sp(`/login?next=${encodeURIComponent(sp(`/checkout?type=rent&content=${c.id}&content_slug=${c.slug}`))}`)
                    : sp(`/checkout?type=rent&content=${c.id}&content_slug=${c.slug}`)}
                  className="bg-black/40 border border-white/15 p-5 hover:-translate-y-0.5 transition-transform"
                  data-testid="pricing-cta-rent"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Rent · 48h</div>
                  <div className="font-display font-bold text-xl mt-2">{formatMoney(rentPrice.amount, rentPrice.currency)}</div>
                  <div className="text-xs text-white/50 mt-1">Watch as many times as you like for 48 hours.</div>
                  <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] border" style={{ borderColor: accentColor, color: accentColor }}>
                    <Clock className="w-3 h-3" /> Rent
                  </div>
                </Link>
              )}
              {buyPrice && (
                <Link
                  to={isLoggedOut
                    ? sp(`/login?next=${encodeURIComponent(sp(`/checkout?type=buy&content=${c.id}&content_slug=${c.slug}`))}`)
                    : sp(`/checkout?type=buy&content=${c.id}&content_slug=${c.slug}`)}
                  className="bg-black/40 border border-white/15 p-5 hover:-translate-y-0.5 transition-transform"
                  data-testid="pricing-cta-buy"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Buy · own forever</div>
                  <div className="font-display font-bold text-xl mt-2">{formatMoney(buyPrice.amount, buyPrice.currency)}</div>
                  <div className="text-xs text-white/50 mt-1">Watch any time, unlimited.</div>
                  <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] border" style={{ borderColor: accentColor, color: accentColor }}>
                    <ShoppingBag className="w-3 h-3" /> Buy
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {hasAnyAccess && (
        <section className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 inline-flex items-center gap-3" data-testid="detail-access-granted">
            <Check className="w-4 h-4 text-emerald-300" />
            <span className="text-emerald-100 text-sm">
              {hasSub ? "Included with your subscription." : owned ? "You own this title." : rented ? "Rented · 48h access." : "Access granted."}
            </span>
          </div>
        </section>
      )}

      {c.kind === "series" && episodesForSeason.length > 0 && (
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-12" data-testid="episodes-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">Episodes</h2>
            {seasonsList.length > 0 && (
              <div className="flex items-center gap-2" data-testid="season-selector">
                {seasonsList.map((s) => (
                  <button
                    key={s.season_number}
                    onClick={() => setSeason(s.season_number)}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] border transition-colors ${season === s.season_number ? "text-black" : "text-white/60 hover:text-white border-white/20"}`}
                    style={season === s.season_number ? { backgroundColor: themeColor, borderColor: themeColor } : {}}
                    data-testid={`season-tab-${s.season_number}`}
                  >
                    Season {s.season_number}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="border border-white/10 bg-[#121212] divide-y divide-white/10">
            {episodesForSeason.map((ep) => (
              <Link
                key={ep.id}
                to={sp(`/watch/${contentSlug}/s${ep.season_number}/e${ep.episode_number}`)}
                className="flex items-center gap-4 p-4 hover:bg-white/5"
                data-testid={`episode-link-${ep.id}`}
              >
                <div className="font-mono text-xs text-white/50 w-12">{String(ep.episode_number).padStart(2, "0")}</div>
                <div className="w-32 h-20 bg-black/40 border border-white/10 overflow-hidden flex-shrink-0 relative group">
                  {(ep.thumbnail_url || ep.video_source?.thumbnail || c.thumbnail_url) ? (
                    <img src={ep.thumbnail_url || ep.video_source?.thumbnail || c.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-white/30"><Film className="w-5 h-5" /></div>
                  )}
                  <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                    <div className="grid place-items-center w-10 h-10 rounded-full" style={{ backgroundColor: themeColor }}>
                      <Play className="w-4 h-4 text-black fill-black" />
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{ep.title}</div>
                  <div className="text-[11px] text-white/50 mt-0.5 line-clamp-2">{ep.description}</div>
                </div>
                <div className="text-xs text-white/40 hidden md:block">{ep.duration_minutes ? `${ep.duration_minutes} min` : ""}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {(c.similar || []).length > 0 && (
        <div className="max-w-[1400px] mx-auto py-6">
          <ContentRow
            name="More like this"
            items={c.similar}
            tenantSlug={tenant.slug}
            themeColor={tenant.theme_color}
            kind="mixed"
          />
        </div>
      )}
    </StorefrontLayout>
  );
}
